import { memo } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Stack } from 'common/ui/Layout';
import { useCompactUI } from 'hooks/useCompactUI';
import { useResizable } from 'hooks/useResizable';
import { useMacroPreview } from '../../RichTextEditor/plugins/macroPlaceholder/useMacroPreview';
import { useSelectedMacroPlaceholder } from '../../RichTextEditor/plugins/macroPlaceholder/useSelectedMacroPlaceholder';
import { getMacroPlaceholderDisplayName } from '../../RichTextEditor/plugins/macroPlaceholder/utils';
import { RightDrawerResizableSection } from './RightDrawerResizableSection';
import { RightDrawerHeaderIcon } from './RightDrawerHeaderIcon';
import { RightDrawerMacroHeader } from './RightDrawerMacroHeader';
import { MacroPreviewBody } from './MacroPreviewBody';

export const RightDrawerMacroSection = memo((): React.ReactElement | null => {
  const { showCompactHeight } = useCompactUI();
  const selectedMacroPlaceholder = useSelectedMacroPlaceholder();
  const macro = useMacroPreview(selectedMacroPlaceholder?.referenceID);
  const minSectionHeight = showCompactHeight ? 40 : 44;
  const collapseSectionHeight = showCompactHeight ? 44 : 48;
  const defaultSectionHeight = showCompactHeight ? 124 : 152;
  const maxSectionHeight = showCompactHeight ? 220 : 280;
  const useResizableProps = useResizable({
    collapseSize: collapseSectionHeight,
    expandSize: defaultSectionHeight,
    minimumSize: minSectionHeight,
    dimension: 'height',
    initialSize: defaultSectionHeight,
  });
  const { openComponent, toggleOpenComponent } = useResizableProps;

  if (macro == null || selectedMacroPlaceholder == null) {
    return null;
  }

  const macroName = macro.macroName || getMacroPlaceholderDisplayName(selectedMacroPlaceholder);
  const { macroContent } = macro;

  return (
    <RightDrawerResizableSection
      currentMaxHeight={maxSectionHeight}
      minSectionHeight={minSectionHeight}
      useResizableProps={useResizableProps}
      expanded={openComponent}
      heading={
        <Stack alignY="center" alignX="between">
          <RightDrawerMacroHeader macroName={macroName} compact={showCompactHeight} />
          <RightDrawerHeaderIcon
            as={openComponent ? ExpandLessIcon : ExpandMoreIcon}
            data-testid="right-drawer-macro-section-toggle"
            onClick={toggleOpenComponent}
            onFocus={toggleOpenComponent}
          />
        </Stack>
      }
    >
      <MacroPreviewBody
        macroContent={macroContent}
        showPreviewLabel
        testID="right-drawer-macro-section"
      />
    </RightDrawerResizableSection>
  );
});

RightDrawerMacroSection.displayName = 'RightDrawerMacroSection';
